import type { Operacao } from "@base/contracts";
import { Dica, cn } from "@base/ui";
import { EXPLICACAO_DO_PAINEL } from "../domain/explicacoes";
import { Clock, Hourglass, Radar, Target, TrendingUp, UserRound, Users } from "lucide-react";
import type { ReactNode } from "react";
import { formatar } from "../domain/formatadores";
import type { Conversao, CustoDaEtapa } from "../domain/funil";

export function TempoDeResposta({ operacao }: { readonly operacao: Operacao }) {
  const resposta = operacao.tempoDeRespostaMinutos;
  return (
    <div className="flex flex-col gap-3 rounded-3xl bg-card px-6 py-5 shadow-sm ring-1 ring-border/70">
      <span className="flex items-center gap-2.5 text-sm font-medium">
        <span className="grid size-7 place-items-center rounded-full bg-accent text-accent-foreground [&_svg]:size-3.5">
          <Clock aria-hidden="true" />
        </span>
        Tempo de resposta
        <Dica rotulo="Como Tempo de resposta é calculado">{EXPLICACAO_DO_PAINEL.resposta}</Dica>
      </span>
      <div className="grid gap-3 sm:grid-cols-2">
        <MiniValor rotulo="Horário comercial" valor={formatar.duracao(resposta?.horarioComercial ?? null)} />
        <MiniValor rotulo="Fora do horário" valor={formatar.duracao(resposta?.foraDoHorario ?? null)} />
      </div>
      <span className="text-xs text-muted-foreground">Mediana até a primeira resposta humana, nos leads do período.</span>
    </div>
  );
}

/**
 * Coluna ao lado do funil: os dois numeros de sintese (CPA e taxa global) e os
 * indicadores operacionais que explicam o funil (ciclo, pipeline, atendimento e
 * rastreio). Indicador sem dado aparece como "—" com a explicacao, nunca zero.
 */
export function PainelLateralDoFunil({
  cpa,
  global,
  operacao,
  metaCpa = null,
  metaGlobal = null,
  leadsNoCrm = null,
  vendas = null,
}: {
  readonly cpa: CustoDaEtapa | undefined;
  readonly global: Conversao;
  readonly operacao: Operacao;
  /** CPA da meta, em reais. */
  readonly metaCpa?: number | null;
  readonly metaGlobal?: number | null;
  readonly leadsNoCrm?: number | null;
  readonly vendas?: number | null;
}) {
  const precisao = operacao.precisaoDoRastreio;

  return (
    <aside aria-label="Indicadores do funil" className="flex h-full flex-col gap-bloco">
      <Sintese
        tom="primario"
        icone={<Target aria-hidden="true" />}
        rotulo="CPA"
        nome="Custo por aquisição de paciente"
        ajuda={EXPLICACAO_DO_PAINEL.cpa}
        valor={formatar.moeda(cpa?.valor ?? null)}
        meta={metaCpa === null ? null : { texto: formatar.moeda(metaCpa), bateu: cpa?.valor != null && cpa.valor <= metaCpa }}
        explicacao={
          cpa?.valor == null
            ? "Sem base para cálculo: não houve venda ou investimento no período."
            : "Investimento do período dividido pelas vendas fechadas."
        }
      />
      <Sintese
        tom="secundario"
        icone={<TrendingUp aria-hidden="true" />}
        rotulo={global.nome}
        nome={global.rotulo}
        ajuda={EXPLICACAO_DO_PAINEL.global}
        valor={formatar.porcentagem(global.taxa)}
        meta={
          metaGlobal === null
            ? null
            : { texto: formatar.porcentagem(metaGlobal), bateu: global.taxa !== null && global.taxa >= metaGlobal }
        }
        explicacao={
          global.taxa === null
            ? "Sem base para cálculo: nenhum lead entrou no CRM no período."
            : leadsNoCrm !== null && vendas !== null
              ? `${formatar.quantidade(vendas)} ${vendas === 1 ? "venda" : "vendas"} para ${formatar.quantidade(leadsNoCrm)} leads que entraram no CRM no período.`
              : "Vendas do período divididas pelos leads que entraram no CRM."
        }
      />

      <div className="mt-auto flex flex-col divide-y divide-border/70 rounded-3xl bg-card px-6 shadow-sm ring-1 ring-border/70">
        <Item
          icone={<Hourglass aria-hidden="true" />}
          rotulo="Ciclo de vendas"
          ajuda={EXPLICACAO_DO_PAINEL.ciclo}
          valor={formatar.dias(operacao.cicloDeVendasDias)}
          detalhe="Mediana do CRM até a venda, nas vendas do período."
        />
        <Item
          icone={<UserRound aria-hidden="true" />}
          rotulo="Pacientes antigos"
          ajuda={EXPLICACAO_DO_PAINEL.antigos}
          valor={formatar.quantidade(operacao.pacientesAntigos)}
          detalhe="Contato criado antes do dia da oportunidade, nos leads do período."
        />
        <Item
          icone={<Users aria-hidden="true" />}
          rotulo="Leads ativos no pipeline"
          ajuda={EXPLICACAO_DO_PAINEL.ativos}
          valor={
            operacao.leadsAtivosNoPipeline === null
              ? "—"
              : formatar.quantidade(operacao.leadsAtivosNoPipeline)
          }
          detalhe="Posição atual, não do fim do período."
        />
        <Item
          icone={<Radar aria-hidden="true" />}
          rotulo="Precisão do rastreio"
          ajuda={EXPLICACAO_DO_PAINEL.rastreio}
          valor={formatar.porcentagem(precisao)}
          detalhe="Leads do período com origem rastreada."
        >
          {precisao !== null ? (
            <div className="h-1.5 overflow-hidden rounded-full bg-muted" aria-hidden="true">
              <div
                className={cn("h-full rounded-full", precisao >= 0.9 ? "bg-success" : "bg-warning")}
                style={{ width: `${precisao * 100}%` }}
              />
            </div>
          ) : null}
        </Item>
      </div>
    </aside>
  );
}

function Item({
  icone,
  rotulo,
  valor,
  detalhe,
  children,
  ajuda,
}: {
  readonly icone: ReactNode;
  readonly rotulo: string;
  readonly valor?: string;
  readonly detalhe: string;
  readonly children?: ReactNode;
  readonly ajuda: string;
}) {
  return (
    <div className="flex flex-col gap-2 py-5">
      <div className="flex items-center justify-between gap-3">
        <span className="flex items-center gap-2.5 text-sm font-medium">
          <span className="grid size-7 place-items-center rounded-full bg-accent text-accent-foreground [&_svg]:size-3.5">
            {icone}
          </span>
          {rotulo}
          <Dica rotulo={`Como ${rotulo} é calculado`}>{ajuda}</Dica>
        </span>
        {valor ? <span className="text-xl font-medium tracking-tight tabular-nums">{valor}</span> : null}
      </div>
      {children}
      <span className="text-xs text-muted-foreground">{detalhe}</span>
    </div>
  );
}

function MiniValor({ rotulo, valor }: { readonly rotulo: string; readonly valor: string }) {
  return (
    <div className="flex flex-col gap-0.5 rounded-xl bg-muted/60 px-3 py-2">
      <span className="text-[11px] text-muted-foreground">{rotulo}</span>
      <span className="text-base font-semibold tabular-nums">{valor}</span>
    </div>
  );
}

function Sintese({
  tom,
  icone,
  rotulo,
  nome,
  valor,
  explicacao,
  meta = null,
  ajuda,
}: {
  readonly tom: "primario" | "secundario";
  readonly icone: ReactNode;
  readonly rotulo: string;
  readonly nome: string;
  readonly valor: string;
  readonly explicacao: string;
  /** Meta do Funil Projetado e se o realizado ja chegou nela. */
  readonly meta?: { readonly texto: string; readonly bateu: boolean } | null;
  readonly ajuda: string;
}) {
  return (
    <div
      className={cn(
        "flex flex-col gap-3 rounded-3xl p-6",
        tom === "primario" ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground",
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <p className="text-xs font-semibold tracking-[0.12em] uppercase opacity-90">
          <span className="flex items-center gap-1.5">
            {rotulo}
            <Dica rotulo={`Como ${rotulo} é calculado`} sobreFundo>
              {ajuda}
            </Dica>
          </span>
          <span className="block pt-1 font-normal tracking-normal normal-case opacity-80">{nome}</span>
        </p>
        <span className="grid size-9 shrink-0 place-items-center rounded-full bg-white/15 [&_svg]:size-4">{icone}</span>
      </div>
      <div className="flex flex-wrap items-end gap-x-3 gap-y-2">
        <p className="text-4xl leading-none font-medium tracking-[-0.04em] tabular-nums">{valor}</p>
        {meta ? (
          <span className="mb-0.5 inline-flex items-center gap-1.5 rounded-full bg-white/15 px-2.5 py-1 text-xs">
            Meta <strong className="font-semibold tabular-nums">{meta.texto}</strong>
            {meta.bateu ? <span aria-label="meta atingida">✓</span> : null}
          </span>
        ) : null}
      </div>
      <p className="text-sm leading-relaxed opacity-85">{explicacao}</p>
    </div>
  );
}
